import LinkAnalysisPage from './pages/LinkAnalysisPage';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuthContext } from './contexts/AuthContext';
import { DateRangeProvider } from './contexts/DateRangeContext';
import { TracerProvider } from './contexts/TracerContext';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Development RBAC testing utilities
import './utils/devAuth';

// Pages
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import DataUploadPage from './pages/DataUploadPage';
import AnalysisPage from './pages/AnalysisPage';
import ResultsPage from './pages/ResultsPage';
import SettingsPage from './pages/SettingsPage';
import PathfinderPage from './pages/PathfinderPage';
import ScheduledAnalysesPage from './pages/ScheduledAnalysesPage';

const App: React.FC = () => {
  const { loading, isAuthenticated } = useAuthContext();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <DateRangeProvider>
      <TracerProvider>
        <Routes>
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        
        {/* Redirect to login if not authenticated */}
        {!isAuthenticated && <Route path="*" element={<Navigate to="/auth/login" replace />} />}
        
        {/* All routes wrapped in Layout with top navigation */}
        <Route
          path="/*"
          element={
            <ProtectedRoute requiredRole="viewer" redirectOnForbidden>
              <Layout>
                <Routes>
                  {/* Default route redirects to Tracer (Pathfinder) */}
                  <Route path="/" element={
                    <ProtectedRoute requiredRole="analyst" redirectOnForbidden>
                      <PathfinderPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Tracer - Main page */}
                  <Route path="/pathfinder" element={<Navigate to="/" replace />} />
                  
                  {/* Redirect /dashboard to Tracer */}
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  
                  {/* Other pages */}
                  <Route path="/upload" element={<DataUploadPage />} />
                  <Route path="/analysis" element={<AnalysisPage />} />
                  <Route path="/scheduled-analyses" element={<ScheduledAnalysesPage />} />
                  <Route path="/results/:jobId?" element={<ResultsPage />} />
                  <Route path="/reports" element={<Navigate to="/results" replace />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/link-analysis" element={<LinkAnalysisPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
        </Routes>
      </TracerProvider>
    </DateRangeProvider>
  );
};

export default App;
